import { num } from "../../../lib/format";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table";
import { Section } from "./section";

export interface FacilityRow {
  id: string;
  category: "RECREATIONAL" | "SPECIAL_FEATURE";
  description: string;
  area: number | null;
  multiple: number | null;
  rate: number | null;
  addGst: boolean;
  totalReinstatementInclFees: number | null;
  reinstatementInflationTotal: number | null;
  demolitionCost: number | null;
}

function sum(rows: FacilityRow[], key: keyof FacilityRow): number {
  return rows.reduce((s, r) => s + (Number(r[key]) || 0), 0);
}

function FacilityTable({
  id,
  title,
  rows,
}: {
  id: string;
  title: string;
  rows: FacilityRow[];
}): React.ReactElement | null {
  if (rows.length === 0) return null;

  return (
    <Section id={id} title={title}>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[200px]">Description</TableHead>
              <TableHead className="text-right">Area</TableHead>
              <TableHead className="text-right">Multiple</TableHead>
              <TableHead className="text-right">Rate</TableHead>
              <TableHead className="text-right">Reinst. Incl Fees</TableHead>
              <TableHead className="text-right">Infl Total</TableHead>
              <TableHead className="text-right">Demo Cost</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="font-medium whitespace-normal max-w-[300px]">{row.description}</TableCell>
                <TableCell className="text-right tabular-nums">{num(row.area)}</TableCell>
                <TableCell className="text-right tabular-nums">{row.multiple?.toFixed(2) ?? "—"}</TableCell>
                <TableCell className="text-right tabular-nums">{num(row.rate)}</TableCell>
                <TableCell className="text-right tabular-nums font-medium">
                  {num(row.totalReinstatementInclFees)}
                </TableCell>
                <TableCell className="text-right tabular-nums">{num(row.reinstatementInflationTotal)}</TableCell>
                <TableCell className="text-right tabular-nums">{num(row.demolitionCost)}</TableCell>
              </TableRow>
            ))}
            <TableRow className="border-t-2 border-foreground/20 bg-muted/50 font-semibold">
              <TableCell>Subtotal</TableCell>
              <TableCell className="text-right tabular-nums">{num(sum(rows, "area"))}</TableCell>
              <TableCell />
              <TableCell />
              <TableCell className="text-right tabular-nums">{num(sum(rows, "totalReinstatementInclFees"))}</TableCell>
              <TableCell className="text-right tabular-nums">{num(sum(rows, "reinstatementInflationTotal"))}</TableCell>
              <TableCell className="text-right tabular-nums">{num(sum(rows, "demolitionCost"))}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </Section>
  );
}

export function SectionFacilities({ facilities }: { facilities: FacilityRow[] }): React.ReactElement | null {
  const recreational = facilities.filter((f) => f.category !== "SPECIAL_FEATURE");
  const special = facilities.filter((f) => f.category === "SPECIAL_FEATURE");

  if (recreational.length === 0 && special.length === 0) return null;

  return (
    <>
      <FacilityTable id="facilities-recreational" title="Recreational Facilities" rows={recreational} />
      <FacilityTable id="facilities-special" title="Special Features" rows={special} />
    </>
  );
}
